import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

interface StatPerson {
  personId: string;
  name: string;
  date: string;
}
interface AdStatistics {
  from: string;
  to: string;
  churchName: string;
  members: { active: number; new: StatPerson[]; ended: number };
  conversions: StatPerson[];
  waterBaptisms: StatPerson[];
  spiritBaptisms: StatPerson[];
  firstVisits: number;
  attendance: { services: number; counted: number; average: number | null; highest: number | null };
  sundaySchool?: { sundays: number; average: number | null; offeringCents: number };
  missing: { conversion: number; waterBaptism: number; spiritBaptism: number };
}

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (y: number, m: number) => new Date(y, m, 0).getDate();
const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short' });

/** El periodo del informe: un mes o el año completo. */
function period(year: number, month: number | null): { from: string; to: string; label: string } {
  if (month === null) return { from: `${year}-01-01`, to: `${year}-12-31`, label: `Año ${year}` };
  return { from: `${year}-${pad(month)}-01`, to: `${year}-${pad(month)}-${pad(lastDay(year, month))}`, label: `${MONTHS[month - 1]} ${year}` };
}

function Names({ people }: { people: StatPerson[] }) {
  if (people.length === 0) return null;
  return (
    <ul className="ad-names">
      {people.map((p) => (
        <li key={`${p.personId}-${p.date}`}>
          <Link to={`/app/personas/${p.personId}`}>{p.name}</Link> <span className="muted small">· {shortDate(p.date)}</span>
        </li>
      ))}
    </ul>
  );
}

/** Asambleas de Dios · Informe estadístico para el distrito (mensual o anual), listo para imprimir. */
export function AdStatisticsPage() {
  const me = useMe();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | null>(now.getMonth() + 1);
  const p = period(year, month);
  const churchId = me.activeChurchId;
  const data = useAsync(
    (signal) => (churchId ? api.get<AdStatistics>(`/multichurch/churches/${churchId}/ad/statistics?from=${p.from}&to=${p.to}`, signal) : Promise.resolve(null)),
    [churchId, p.from, p.to],
  );
  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="page ad-report">
      <header className="page__header">
        <p className="eyebrow">Asambleas de Dios</p>
        <h1>Informe estadístico</h1>
        <p className="muted">Para enviar al distrito. Sale de los expedientes de los miembros, la asistencia a los cultos y los visitantes registrados en KEORA.</p>
      </header>

      <div className="actions actions--wrap no-print" role="group" aria-label="Periodo">
        <div className="field">
          <label htmlFor="ad-month">Mes</label>
          <select id="ad-month" value={month ?? 0} onChange={(e) => setMonth(Number(e.target.value) || null)}>
            <option value={0}>Todo el año</option>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="ad-year">Año</label>
          <select id="ad-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <Button variant="secondary" onClick={() => window.print()}>
          Imprimir o guardar PDF
        </Button>
      </div>

      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && !data.data ? <EmptyState title="Elige primero una iglesia en el selector de arriba." /> : null}
      {data.status === 'ready' && data.data ? <Report s={data.data} label={p.label} /> : null}
      {churchId ? <ReportSharing churchId={churchId} canManage={me.can('church.manage')} /> : null}
    </div>
  );
}

/** La iglesia decide si su sección o distrito ve su informe (solo números, sin nombres). */
function ReportSharing({ churchId, canManage }: { churchId: string; canManage: boolean }) {
  const base = `/multichurch/churches/${churchId}/ad/report-sharing`;
  const data = useAsync((signal) => api.get<{ items: { id: string; name: string; kind: string; shared: boolean; overseers: string[] }[] }>(base, signal).then((r) => r.items), [base]);
  const [error, setError] = useState<string | null>(null);
  if (data.status !== 'ready') return null;
  return (
    <Card title="Compartir con mi sección o distrito">
      <p className="small muted no-print">
        Si lo activas, el presbítero de esa red ve los números de tu informe (miembros, convertidos, bautizados, asistencia), nunca los nombres. Tu iglesia se une a su
        sección o distrito en Comunidad → Red de iglesias.
      </p>
      {error ? <Alert>{error}</Alert> : null}
      {data.data.length === 0 ? <p className="small muted">Tu iglesia todavía no está en ninguna red.</p> : null}
      <ul className="list">
        {data.data.map((n) => (
          <li key={n.id} className="list__item">
            <label className="check">
              <input
                type="checkbox"
                disabled={!canManage}
                checked={n.shared}
                onChange={(e) => {
                  setError(null);
                  api.put(`${base}/${n.id}`, { on: e.target.checked }).then(data.reload).catch((err) => setError(errorMessage(err)));
                }}
              />
              <span>
                {n.name}
                {n.overseers.length ? <span className="small muted"> · {n.overseers.join(', ')}</span> : null}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {!canManage && data.data.length ? <p className="small muted no-print">Solo la administración de la iglesia decide si se comparte</p> : null}
    </Card>
  );
}

function Report({ s, label }: { s: AdStatistics; label: string }) {
  const tiles: [string, number | string][] = [
    ['Miembros activos', s.members.active],
    ['Miembros nuevos', s.members.new.length],
    ['Convertidos', s.conversions.length],
    ['Bautizados en agua', s.waterBaptisms.length],
    ['Bautizados en el Espíritu Santo', s.spiritBaptisms.length],
    ['Asistencia promedio', s.attendance.average ?? '—'],
    ['Visitantes nuevos', s.firstVisits],
    ['Escuela Dominical (promedio)', s.sundaySchool?.average ?? '—'],
  ];
  const missing = s.missing.conversion + s.missing.waterBaptism + s.missing.spiritBaptism;
  return (
    <>
      <Card title={`${s.churchName} · ${label}`}>
        <ul className="ad-tiles" aria-label="Resumen">
          {tiles.map(([k, v]) => (
            <li key={k}>
              <strong>{v}</strong>
              <span>{k}</span>
            </li>
          ))}
        </ul>
        <p className="small muted">
          Asistencia: {s.attendance.services} {s.attendance.services === 1 ? 'culto' : 'cultos'} en el periodo, {s.attendance.counted} con conteo
          {s.attendance.highest !== null ? ` · la más alta: ${s.attendance.highest}` : ''}. Membresías que terminaron: {s.members.ended}.
        </p>
      </Card>

      <div className="stack-grid">
        <Card title={`Convertidos (${s.conversions.length})`}>
          {s.conversions.length ? <Names people={s.conversions} /> : <p className="muted small">Nadie registrado en este periodo.</p>}
        </Card>
        <Card title={`Bautizados en agua (${s.waterBaptisms.length})`}>
          {s.waterBaptisms.length ? <Names people={s.waterBaptisms} /> : <p className="muted small">Nadie registrado en este periodo.</p>}
        </Card>
        <Card title={`Bautizados en el Espíritu Santo (${s.spiritBaptisms.length})`}>
          {s.spiritBaptisms.length ? <Names people={s.spiritBaptisms} /> : <p className="muted small">Nadie registrado en este periodo.</p>}
        </Card>
        <Card title={`Miembros nuevos (${s.members.new.length})`}>
          {s.members.new.length ? <Names people={s.members.new} /> : <p className="muted small">Nadie en este periodo.</p>}
        </Card>
      </div>

      {missing > 0 ? (
        <Card title="Para que el informe salga completo">
          <p className="small">
            De los miembros activos, faltan datos en su expediente: {s.missing.conversion} sin fecha de conversión, {s.missing.waterBaptism} sin fecha de
            bautismo en agua y {s.missing.spiritBaptism} sin fecha de bautismo en el Espíritu Santo (si ya lo recibieron).
          </p>
          <p className="small muted no-print">
            Se llenan en <Link to="/app/miembros">Miembros</Link> → la ficha de cada persona → Expediente.
          </p>
        </Card>
      ) : null}
    </>
  );
}
